import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Download, Share2, MessageCircle, Mail, Phone, Loader2, Check, QrCode, Calendar, MapPin, Clock, User, FileDown } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Beautiful Event Ticket Component (SOYA Awards Style)
const EventTicket = React.forwardRef(({ ticket, event, index = 0 }, ref) => {
  // The backend provides QR code as base64 image, or we can use ticket_number to generate
  const getQRCodeSource = () => {
    if (ticket?.qr_code && ticket.qr_code.length > 50) {
      // It's a base64 image
      return { type: 'base64', src: `data:image/png;base64,${ticket.qr_code}` };
    }
    // Use ticket number to generate QR
    return { type: 'generate', value: ticket?.qr_code || ticket?.ticket_number || 'TICKET' };
  };

  const qrSource = getQRCodeSource();

  // Get theme color based on event type - MORE VIBRANT COLORS
  const getThemeColor = () => {
    const type = event?.event_type?.toLowerCase() || '';
    const name = event?.name?.toLowerCase() || '';
    
    if (name.includes('wellness') || name.includes('mental'))
      return { 
        primary: '#0891b2', // Vibrant cyan
        secondary: '#22d3ee', 
        accent: '#ecfeff', 
        text: '#0e7490',
        gradient: 'linear-gradient(135deg, #0891b2 0%, #06b6d4 50%, #22d3ee 100%)',
        bgGradient: 'linear-gradient(135deg, #ecfeff 0%, #cffafe 100%)'
      };
    if (type.includes('conference') || name.includes('conference')) 
      return { 
        primary: '#2563eb', // Vibrant blue
        secondary: '#3b82f6', 
        accent: '#dbeafe', 
        text: '#1e40af',
        gradient: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #60a5fa 100%)',
        bgGradient: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)'
      };
    if (type.includes('award') || type.includes('gala')) 
      return { 
        primary: '#d97706', // Vibrant gold
        secondary: '#fbbf24', 
        accent: '#fef3c7', 
        text: '#92400e',
        gradient: 'linear-gradient(135deg, #b45309 0%, #d97706 50%, #fbbf24 100%)',
        bgGradient: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)'
      };
    if (type.includes('sport') || type.includes('tournament')) 
      return { 
        primary: '#16a34a', // Vibrant green
        secondary: '#22c55e', 
        accent: '#dcfce7', 
        text: '#166534',
        gradient: 'linear-gradient(135deg, #15803d 0%, #22c55e 50%, #4ade80 100%)',
        bgGradient: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)'
      };
    if (type.includes('concert') || type.includes('festival')) 
      return { 
        primary: '#ea580c', // Vibrant orange
        secondary: '#f97316', 
        accent: '#ffedd5', 
        text: '#c2410c',
        gradient: 'linear-gradient(135deg, #c2410c 0%, #ea580c 50%, #fb923c 100%)',
        bgGradient: 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)'
      };
    if (name.includes('golf'))
      return { 
        primary: '#059669', 
        secondary: '#10b981', 
        accent: '#d1fae5', 
        text: '#047857',
        gradient: 'linear-gradient(135deg, #047857 0%, #10b981 50%, #34d399 100%)',
        bgGradient: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)'
      };
    // Default: Vibrant green
    return { 
      primary: '#16a34a', 
      secondary: '#22c55e', 
      accent: '#dcfce7', 
      text: '#166534',
      gradient: 'linear-gradient(135deg, #15803d 0%, #22c55e 50%, #4ade80 100%)',
      bgGradient: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)'
    };
  };

  const theme = getThemeColor();

  const formatDate = (dateStr) => {
    if (!dateStr) return 'TBA';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  return (
    <motion.div 
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      className="w-full max-w-md mx-auto bg-white rounded-3xl overflow-hidden shadow-2xl"
      style={{ fontFamily: "'Segoe UI', system-ui, sans-serif" }}
      data-testid={`ticket-card-${index}`}
    >
      {/* Event Hero Image / Poster */}
      {event?.hero_image_url && (
        <div className="relative h-40 overflow-hidden">
          <img 
            src={event.hero_image_url} 
            alt={event.name}
            className="w-full h-full object-cover"
          />
          <div 
            className="absolute inset-0"
            style={{ background: 'linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.6) 100%)' }}
          />
          {/* Verified Badge on hero */}
          <div className="absolute top-3 right-3">
            <div className="flex items-center gap-1 px-3 py-1.5 bg-white/90 backdrop-blur rounded-full shadow-lg">
              <Check className="w-4 h-4 text-green-600" />
              <span className="text-xs font-bold text-green-700">VERIFIED</span>
            </div>
          </div>
        </div>
      )}

      {/* Top Decorative Border (only if no hero image) */}
      {!event?.hero_image_url && (
        <div 
          className="h-3"
          style={{ background: theme.gradient }}
        />
      )}
      
      {/* Header with Logo/Branding */}
      <div 
        className="relative py-6 px-6"
        style={{ 
          background: theme.bgGradient
        }}
      >
        {/* Event Logo */}
        {event?.logo_url ? (
          <img 
            src={event.logo_url} 
            alt={event.name}
            className="mx-auto h-20 object-contain drop-shadow-md"
          />
        ) : (
          <div className="text-center">
            <div 
              className="w-20 h-20 rounded-2xl mx-auto flex items-center justify-center text-3xl font-bold text-white shadow-xl"
              style={{ background: theme.gradient }}
            >
              {event?.name?.charAt(0) || 'E'}
            </div>
          </div>
        )}
        
        {/* Verified Badge (if no hero image) */}
        {!event?.hero_image_url && (
          <div className="absolute top-4 right-4">
            <div className="flex items-center gap-1 px-3 py-1.5 bg-white rounded-full shadow-md">
              <Check className="w-4 h-4 text-green-600" />
              <span className="text-xs font-bold text-green-700">VERIFIED</span>
            </div>
          </div>
        )}
      </div>

      {/* Event Title Banner */}
      <div 
        className="py-5 px-6 text-center"
        style={{ background: theme.gradient }}
      >
        <p className="text-white/90 text-xs tracking-[0.25em] uppercase font-semibold">
          {event?.event_type || 'EVENT'} • {event?.start_date ? new Date(event.start_date).getFullYear() : new Date().getFullYear()}
        </p>
        <h2 className="text-white text-xl font-bold mt-2 leading-tight drop-shadow-sm">
          {event?.name || 'Event'}
        </h2>
      </div>

      {/* Attendee Name Section */}
      <div 
        className="px-6 pt-6 pb-4 text-center"
        style={{ background: theme.bgGradient }}
      >
        <p className="text-gray-500 text-xs uppercase tracking-widest mb-2 font-medium">✨ This ticket admits ✨</p>
        <h1 
          className="text-3xl font-bold mb-1"
          style={{ color: theme.primary }}
        >
          {ticket?.attendee_name || 'Guest'}
        </h1>
        {ticket?.organization && (
          <p className="text-gray-600 text-sm font-medium">{ticket.organization}</p>
        )}
      </div>

      {/* Perforated Divider */}
      <div className="relative">
        <div className="absolute left-0 w-4 h-8 bg-gray-100 rounded-r-full -ml-1" />
        <div className="absolute right-0 w-4 h-8 bg-gray-100 rounded-l-full -mr-1" />
        <div className="mx-6 border-t-2 border-dashed border-gray-200" />
      </div>

      {/* Details & QR Code Section */}
      <div className="px-6 py-5 flex gap-4">
        {/* Event Details */}
        <div className="flex-1 space-y-3 text-sm">
          <div className="flex items-start gap-3">
            <Calendar className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-gray-400 text-xs uppercase">Date</p>
              <p className="font-semibold text-gray-800">{formatDate(event?.start_date)}</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Clock className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-gray-400 text-xs uppercase">Time</p>
              <p className="font-semibold text-gray-800">{formatTime(event?.start_date) || 'TBA'}</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-gray-400 text-xs uppercase">Venue</p>
              <p className="font-semibold text-gray-800">{event?.venue_name || 'TBA'}</p>
              {event?.city && <p className="text-gray-500 text-xs">{event.city}</p>}
            </div>
          </div>
        </div>

        {/* QR Code */}
        <div className="flex flex-col items-center justify-center">
          {qrSource.type === 'base64' ? (
            <div className="p-2 bg-white rounded-lg border-2 border-gray-100 shadow-sm">
              <img 
                src={qrSource.src} 
                alt="QR Code" 
                className="w-28 h-28"
                data-testid="ticket-qr-code"
              />
            </div>
          ) : qrSource.value ? (
            <div className="p-2 bg-white rounded-lg border-2 border-gray-100 shadow-sm">
              <QRCodeSVG 
                value={qrSource.value}
                size={112}
                level="M"
                data-testid="ticket-qr-code"
              />
            </div>
          ) : (
            <div className="w-32 h-32 bg-gray-100 rounded-lg flex items-center justify-center">
              <QrCode className="w-10 h-10 text-gray-300" />
            </div>
          )}
          <p className="text-xs text-gray-400 mt-2 text-center">Scan at entrance</p>
        </div>
      </div>

      {/* Ticket Type Badge */}
      {ticket?.ticket_type && (
        <div className="px-6 pb-3">
          <div 
            className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium"
            style={{ backgroundColor: theme.accent, color: theme.text }}
          >
            {ticket.ticket_type}
          </div>
        </div>
      )}

      {/* Partner Logos - Show if event has partner_logos_url */}
      {event?.partner_logos_url && (
        <div className="px-4 py-4 bg-gradient-to-b from-white to-gray-50 border-t border-gray-100">
          <p className="text-center text-xs text-gray-400 uppercase tracking-wider mb-2 font-medium">In Partnership With</p>
          <img 
            src={event.partner_logos_url} 
            alt="Event Partners" 
            className="w-full h-auto object-contain max-h-20"
            data-testid="partner-logos"
          />
        </div>
      )}

      {/* Footer Banner */}
      <div 
        className="py-4 text-center"
        style={{ background: theme.gradient }}
      >
        <p className="text-white text-sm tracking-[0.3em] uppercase font-bold drop-shadow-sm">
          🎫 Card Admits One 🎫
        </p>
      </div>

      {/* Ticket Number */}
      <div className="bg-gray-50 py-3 px-6 text-center border-t">
        <p className="text-xs text-gray-400 font-mono">
          Ticket #{ticket?.ticket_number?.slice(-10).toUpperCase() || 'XXXXXXXXXX'}
        </p>
      </div>
    </motion.div>
  );
});

// Main Ticket View Page
const TicketViewPage = () => {
  const { ticketId } = useParams();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('order');
  
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sharing, setSharing] = useState(false);
  const [sendingSms, setSendingSms] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const ticketRefs = useRef([]);
  
  useEffect(() => {
    const fetchData = async () => {
      const id = ticketId || orderId;
      if (!id) {
        setLoading(false);
        return;
      }
      
      try {
        const response = await fetch(`${API}/universal-events/tickets/${id}`);
        if (response.ok) {
          const result = await response.json();
          setData(result);
        }
      } catch (err) {
        console.error('Error fetching ticket:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [ticketId, orderId]);

  // Generate PDF for ticket(s)
  const generatePDF = async (ticketIndex = null) => {
    setGeneratingPdf(true);
    toast.info('Generating PDF...');
    
    try {
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      const tickets = data?.tickets || (data?.ticket ? [data.ticket] : []);
      const indicesToProcess = ticketIndex !== null ? [ticketIndex] : tickets.map((_, i) => i);
      
      for (let i = 0; i < indicesToProcess.length; i++) {
        const idx = indicesToProcess[i];
        const ticketElement = ticketRefs.current[idx];
        
        if (!ticketElement) continue;
        
        // Add new page for subsequent tickets
        if (i > 0) {
          pdf.addPage();
        }
        
        // Generate canvas from ticket element
        const canvas = await html2canvas(ticketElement, {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          logging: false
        });
        
        const imgData = canvas.toDataURL('image/png');
        
        // Calculate dimensions to fit on A4 with margins
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const margin = 15;
        const maxWidth = pageWidth - (margin * 2);
        const maxHeight = pageHeight - (margin * 2);
        
        const imgWidth = canvas.width;
        const imgHeight = canvas.height;
        const ratio = Math.min(maxWidth / imgWidth, maxHeight / imgHeight);
        
        const finalWidth = imgWidth * ratio;
        const finalHeight = imgHeight * ratio;
        
        // Center the ticket on the page
        const x = (pageWidth - finalWidth) / 2;
        const y = (pageHeight - finalHeight) / 2;
        
        pdf.addImage(imgData, 'PNG', x, y, finalWidth, finalHeight);
      }
      
      // Generate filename
      const eventName = data?.event?.name?.replace(/[^a-zA-Z0-9]/g, '_') || 'Ticket';
      const ticketNum = tickets[0]?.ticket_number?.slice(-6) || 'ticket';
      const fileName = `${eventName}_${ticketNum}.pdf`;
      
      pdf.save(fileName);
      toast.success('PDF downloaded successfully!');
      
    } catch (err) {
      console.error('PDF generation error:', err);
      toast.error('Failed to generate PDF');
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleShare = async (method) => {
    setSharing(true);
    const ticketUrl = window.location.href;
    const eventName = data?.event?.name || 'Event';
    const message = `Check out my ticket for ${eventName}! View at: ${ticketUrl}`;
    
    try {
      switch (method) {
        case 'whatsapp':
          window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
          break;
        case 'sms':
          window.open(`sms:?body=${encodeURIComponent(message)}`, '_blank');
          break;
        case 'email':
          window.open(`mailto:?subject=${encodeURIComponent(`My Ticket for ${eventName}`)}&body=${encodeURIComponent(message)}`, '_blank');
          break;
        case 'copy':
          await navigator.clipboard.writeText(ticketUrl);
          toast.success('Ticket link copied to clipboard!');
          break;
        default:
          break;
      }
    } catch (err) {
      toast.error('Failed to share');
    } finally {
      setSharing(false);
    }
  };

  const sendToPhone = async (channel = 'sms') => {
    if (!data?.order?.id && !data?.ticket?.id) return;
    
    const orderId = data.order?.id;
    if (!orderId) {
      toast.error('Unable to send notification - order not found');
      return;
    }
    
    setSendingSms(true);
    try {
      const response = await fetch(`${API}/universal-events/orders/${orderId}/send-ticket`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ channel })
      });
      
      if (response.ok) {
        toast.success(`Ticket sent via ${channel.toUpperCase()}!`);
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to send notification');
      }
    } catch (err) {
      toast.error('Failed to send notification');
    } finally {
      setSendingSms(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-100 via-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-green-600 mx-auto mb-4" />
          <p className="text-gray-500">Loading your ticket...</p>
        </div>
      </div>
    );
  }

  const tickets = data?.tickets || (data?.ticket ? [data.ticket] : []);
  const event = data?.event;
  const order = data?.order;

  if (!tickets.length && !event) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-100 via-gray-50 to-gray-100 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
            <QrCode className="w-10 h-10 text-gray-400" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-3">Ticket Not Found</h2>
          <p className="text-gray-500 mb-6">
            The ticket you're looking for doesn't exist or may have been removed.
          </p>
          <Button 
            onClick={() => window.location.href = '/events'}
            className="bg-green-600 hover:bg-green-700"
          >
            Browse Events
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-white to-teal-50 py-8 px-4">
      <div className="max-w-lg mx-auto">
        {/* Success Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-cyan-500 via-teal-500 to-emerald-500 rounded-3xl p-6 mb-6 flex items-center gap-4 shadow-xl"
          data-testid="ticket-success-header"
        >
          <div className="w-16 h-16 bg-white/25 backdrop-blur-sm rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg">
            <Check className="w-10 h-10 text-white drop-shadow-md" />
          </div>
          <div>
            <p className="font-bold text-white text-xl drop-shadow-sm">🎉 Ticket Confirmed!</p>
            <p className="text-white/90 text-sm mt-1">Present this QR code at the entrance</p>
            {order?.order_number && (
              <p className="text-white/70 text-xs mt-2 font-mono bg-white/10 inline-block px-2 py-0.5 rounded">Order: {order.order_number}</p>
            )}
          </div>
        </motion.div>

        {/* Tickets */}
        <div className="space-y-6">
          {tickets.map((ticket, index) => (
            <EventTicket 
              key={ticket.id || index} 
              ref={el => ticketRefs.current[index] = el}
              ticket={ticket} 
              event={event}
              index={index}
            />
          ))}
        </div>

        {/* Share Options */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-8 bg-white rounded-2xl p-5 shadow-lg"
        >
          <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
            <Share2 className="w-5 h-5 text-gray-600" />
            Share Your Ticket
          </h3>
          <div className="grid grid-cols-4 gap-3">
            <button
              onClick={() => handleShare('whatsapp')}
              disabled={sharing}
              className="flex flex-col items-center gap-2 p-4 rounded-xl bg-green-50 hover:bg-green-100 transition-all duration-200 hover:scale-105"
              data-testid="share-whatsapp"
            >
              <MessageCircle className="w-6 h-6 text-green-600" />
              <span className="text-xs font-medium text-green-700">WhatsApp</span>
            </button>
            <button
              onClick={() => handleShare('sms')}
              disabled={sharing}
              className="flex flex-col items-center gap-2 p-4 rounded-xl bg-blue-50 hover:bg-blue-100 transition-all duration-200 hover:scale-105"
              data-testid="share-sms"
            >
              <Phone className="w-6 h-6 text-blue-600" />
              <span className="text-xs font-medium text-blue-700">SMS</span>
            </button>
            <button
              onClick={() => handleShare('email')}
              disabled={sharing}
              className="flex flex-col items-center gap-2 p-4 rounded-xl bg-purple-50 hover:bg-purple-100 transition-all duration-200 hover:scale-105"
              data-testid="share-email"
            >
              <Mail className="w-6 h-6 text-purple-600" />
              <span className="text-xs font-medium text-purple-700">Email</span>
            </button>
            <button
              onClick={() => handleShare('copy')}
              disabled={sharing}
              className="flex flex-col items-center gap-2 p-4 rounded-xl bg-gray-50 hover:bg-gray-100 transition-all duration-200 hover:scale-105"
              data-testid="share-copy"
            >
              <Download className="w-6 h-6 text-gray-600" />
              <span className="text-xs font-medium text-gray-700">Copy Link</span>
            </button>
          </div>
        </motion.div>

        {/* Send to Phone */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-4 bg-white rounded-2xl p-5 shadow-lg"
        >
          <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
            <Phone className="w-5 h-5 text-gray-600" />
            Send to My Phone
          </h3>
          <p className="text-gray-500 text-sm mb-4">
            Get your ticket link delivered directly to your registered phone number.
          </p>
          <div className="flex gap-3">
            <Button 
              variant="outline"
              onClick={() => sendToPhone('sms')}
              disabled={sendingSms}
              className="flex-1"
              data-testid="send-sms-btn"
            >
              {sendingSms ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Phone className="w-4 h-4 mr-2" />
              )}
              Send via SMS
            </Button>
            <Button 
              onClick={() => sendToPhone('whatsapp')}
              disabled={sendingSms}
              className="flex-1 bg-green-600 hover:bg-green-700"
              data-testid="send-whatsapp-btn"
            >
              {sendingSms ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <MessageCircle className="w-4 h-4 mr-2" />
              )}
              Send via WhatsApp
            </Button>
          </div>
        </motion.div>

        {/* Download Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-6 space-y-3"
        >
          <Button 
            className="w-full bg-gray-900 hover:bg-gray-800 text-white py-6 text-lg font-semibold shadow-lg"
            onClick={() => generatePDF()}
            disabled={generatingPdf}
            data-testid="download-pdf-btn"
          >
            {generatingPdf ? (
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            ) : (
              <FileDown className="w-5 h-5 mr-2" />
            )}
            {generatingPdf ? 'Generating PDF...' : 'Download as PDF'}
          </Button>
          <Button 
            variant="outline"
            className="w-full py-4"
            onClick={() => window.print()}
            data-testid="print-ticket-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Print Ticket
          </Button>
        </motion.div>

        {/* Footer Note */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="text-center text-gray-400 text-sm mt-6"
        >
          Please present this ticket at the venue entrance. <br />
          Save the PDF for offline access.
        </motion.p>
      </div>
    </div>
  );
};

export default TicketViewPage;
